import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Student } from "./useStudents";

export interface StudentAccountsResponseDto {
    studentAccountNumber: string; // UUID
    student: Student;
    dueAmount: number; // BigDecimal
}

const API_URL = `${process.env.REACT_APP_API_URL}/student-accounts`;

const getStudentAccountsByClassIdAndSessionId = async (classId: number, sessionId: number): Promise<StudentAccountsResponseDto[]> => {
    try {
        const response = await axios.get(`${API_URL}/${classId}/${sessionId}`);
        return response.data;
    } catch (error: any) {
        if (error?.response?.status === 404) {
            return [];
        }
        throw error;
    }
}

const createStudentAccount = async (studentDto: Student): Promise<string> => {
    const response = await axios.post(API_URL, studentDto);
    return response.data;
}

export const useStudentAccounts = (classId?: number | null, sessionId?: number | null, enabled: boolean = true) => {
    return useQuery<StudentAccountsResponseDto[], Error>({
        queryKey: ["studentAccounts", classId, sessionId],
        queryFn: () => getStudentAccountsByClassIdAndSessionId(classId!, sessionId!),
        enabled: enabled && !!classId && !!sessionId,
        staleTime: 30000,
        retry: false,
        refetchOnWindowFocus: false,
    });
};

export const useCreateStudentAccount = () => {
    const queryClient = useQueryClient();
    return useMutation<string, Error, Student>({
        mutationFn: createStudentAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
        },
    });
};

export interface StudentAccountAmountUpdateDto {
    studentAccountNumber: string;
    student: Student;
    feeAmount: string;
    updatedFeeAmount: string;
    description: string;
    updatedBy: string;
}

const updateStudentAccountAmount = async (dto: StudentAccountAmountUpdateDto): Promise<void> => {
    await axios.put(`${API_URL}/update-fee`, dto);
};

export const useUpdateStudentAccountAmount = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, StudentAccountAmountUpdateDto>({
        mutationFn: updateStudentAccountAmount,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
        },
    });
};
